import Empty from 'constants/empty';
import { SAVE_FOR_LATER_LIST_TYPE, ACTION_TYPES } from 'constants/saveForLater';
import rwdBasket from 'reducers/rwdBasket';
import inlineBasket from 'reducers/inline-basket';

const { UPDATE_BASKET } = rwdBasket.ACTION_TYPES;
const { PRODUCT_ADDED_TO_BASKET } = inlineBasket.ACTION_TYPES;

const initialState = {
    loading: false,
    error: null,
    lists: Empty.Array,
    listId: null,
    listName: null,
    shareLink: null,
    itemCount: 0,
    items: Empty.Array,
    optimisticItems: Empty.Array,
    movingSkuIds: Empty.Array,
    lastFetchedAt: null,
    confirmation: null,
    basketConfirmation: null
};

const getPrimarySaveForLaterList = lists => {
    if (!Array.isArray(lists)) {
        return null;
    }

    const normalized = lists.find(list => (list.shoppingListType || '').toLowerCase() === SAVE_FOR_LATER_LIST_TYPE) || lists[0];

    if (!normalized) {
        return null;
    }

    return {
        id: normalized.shoppingListId || normalized.id || null,
        name: normalized.shoppingListName || normalized.name || null,
        shareLink: normalized.shareLink || null,
        itemCount: normalized.shoppingListItemsCount || 0
    };
};

// eslint-disable-next-line complexity
const reducer = function (state = initialState, action) {
    switch (action.type) {
        case ACTION_TYPES.SET_SAVE_FOR_LATER_LOADING: {
            return { ...state, loading: action.loading };
        }
        case ACTION_TYPES.SET_SAVE_FOR_LATER_ERROR: {
            return { ...state, error: action.error, loading: false };
        }
        case ACTION_TYPES.UPDATE_SAVE_FOR_LATER_LISTS: {
            const lists = Array.isArray(action.lists) ? action.lists : Empty.Array;
            const primaryList = getPrimarySaveForLaterList(lists);

            return {
                ...state,
                lists,
                listId: primaryList?.id || null,
                listName: primaryList?.name || null,
                shareLink: primaryList?.shareLink || null,
                itemCount: primaryList?.itemCount || 0,
                loading: false
            };
        }
        case ACTION_TYPES.UPDATE_SAVE_FOR_LATER_ITEMS: {
            const items = Array.isArray(action.items) ? action.items : Empty.Array;
            const currentSkuIds = new Set(items.map(item => item.skuId || item.sku?.skuId).filter(Boolean));

            // Clean up movingSkuIds for items that are no longer in the list
            const cleanedMovingSkuIds = state.movingSkuIds.filter(skuId => currentSkuIds.has(skuId));

            return {
                ...state,
                items,
                movingSkuIds: cleanedMovingSkuIds.length === state.movingSkuIds.length ? state.movingSkuIds : cleanedMovingSkuIds,
                lastFetchedAt: Date.now(),
                loading: false
            };
        }
        case ACTION_TYPES.ADD_SAVE_FOR_LATER_ITEM_OPTIMISTIC: {
            const incoming = action.item || Empty.Object;
            const skuId = incoming.skuId || incoming.sku?.skuId;

            if (!skuId) {
                return state;
            }

            const alreadyExists = state.optimisticItems.some(item => (item.skuId || item.sku?.skuId) === skuId);

            if (alreadyExists) {
                return state;
            }

            return {
                ...state,
                optimisticItems: [...state.optimisticItems, incoming],
                items: [incoming, ...state.items.filter(item => (item.skuId || item.sku?.skuId) !== skuId)]
            };
        }
        case ACTION_TYPES.REMOVE_SAVE_FOR_LATER_ITEM_OPTIMISTIC: {
            const skuId = action.skuId;

            if (!skuId) {
                return state;
            }

            // Only remove from optimisticItems - the 'items' array should only be updated via UPDATE_SAVE_FOR_LATER_ITEMS action
            return {
                ...state,
                optimisticItems: state.optimisticItems.filter(item => (item.skuId || item.sku?.skuId) !== skuId)
            };
        }
        case ACTION_TYPES.SET_MOVING_SKU: {
            const skuId = action.skuId;

            if (!skuId) {
                return state;
            }

            // Add SKU ID to movingSkuIds if not already present
            const alreadyMoving = state.movingSkuIds.includes(skuId);

            if (alreadyMoving) {
                return state;
            }

            return {
                ...state,
                movingSkuIds: [...state.movingSkuIds, skuId]
            };
        }
        case ACTION_TYPES.REMOVE_MOVING_SKU: {
            const skuId = action.skuId;

            if (!skuId) {
                return state;
            }

            return {
                ...state,
                movingSkuIds: state.movingSkuIds.filter(id => id !== skuId)
            };
        }
        case ACTION_TYPES.CLEAR_MOVING_SKUS: {
            return {
                ...state,
                movingSkuIds: Empty.Array
            };
        }
        case ACTION_TYPES.SET_SAVE_FOR_LATER_CONFIRMATION: {
            return {
                ...state,
                confirmation: action.confirmationData || null
            };
        }
        case ACTION_TYPES.CLEAR_SAVE_FOR_LATER_CONFIRMATION: {
            return {
                ...state,
                confirmation: null
            };
        }
        case ACTION_TYPES.SET_BASKET_CONFIRMATION: {
            return {
                ...state,
                basketConfirmation: action.confirmationData || null
            };
        }
        case ACTION_TYPES.CLEAR_BASKET_CONFIRMATION: {
            return {
                ...state,
                basketConfirmation: null
            };
        }
        case UPDATE_BASKET:
        case PRODUCT_ADDED_TO_BASKET: {
            return {
                ...state,
                basketConfirmation: null
            };
        }
        default: {
            return state;
        }
    }
};

reducer.ACTION_TYPES = ACTION_TYPES;
reducer.getPrimarySaveForLaterList = getPrimarySaveForLaterList;

export default reducer;
